import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { leadsApi, type Lead, type LeadCreateRequest, type LeadSourceOption } from '../api/leads'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { campaignsApi, type Campaign } from '../api/campaigns'
import { DuplicateConflictError, newIdempotencyKey, type DuplicateCandidate } from '../api/client'
import {
  FormGrid,
  Button, Callout, Card, CardContent, LoadingState, Page, PageHeader, Select, StatusPill, TextField,
} from '../components/ui'

/** API route segment for each `DuplicateCandidate.entityType` value this endpoint's
 * duplicate-detection can return (see `DuplicateResponses.cs` on the backend). Same convention as
 * `CompanyFormPage.tsx`/`CustomerFormPage.tsx`/`LeadDetailPage.tsx` — duplicated per file, not
 * imported (no shared module for it in this repo). Unlike `LeadDetailPage.tsx`'s own copy (which
 * serves its Lead-to-Customer conversion flow and checks Customer/Person/Company/Contact
 * candidates, never Lead), this endpoint's own candidates can carry `entityType: "Lead"`, so this
 * map adds that entry. */
const ENTITY_TYPE_API_SEGMENT: Record<string, string> = {
  Person: 'people',
  Company: 'companies',
  Contact: 'contacts',
  Customer: 'customers',
  Lead: 'leads',
}

function candidateApiUrl(c: DuplicateCandidate): string {
  const segment = ENTITY_TYPE_API_SEGMENT[c.entityType] ?? c.entityType.toLowerCase()
  return `/api/${segment}/${c.id}`
}

interface Props {
  mode: 'create' | 'edit'
}

interface LeadDetail extends Lead {
  priority?: string
  estimatedValue?: number
}

interface FormState {
  firstName: string
  lastName: string
  companyName: string
  email: string
  phone: string
  // TASK-0149 (REQ-066-001). Typed as plain `string` (never `string | undefined`) like every other
  // member here: this is controlled-input state, and an undefined value would flip the input from
  // controlled to uncontrolled mid-edit. The '' -> omitted conversion happens once, in handleSave.
  fiscalCode: string
  vatNumber: string
  // TASK-0198 (REQ-063-001). Same reasoning as `fiscalCode`/`vatNumber` above: plain `string`
  // controlled-input state, '' -> omitted conversion happens once, in handleSave.
  externalLeadId: string
  // TASK-0210 (REQ-063-001). Same reasoning as `externalLeadId` above: plain `string`
  // controlled-input state, '' -> omitted conversion happens once, in handleSave.
  landingPage: string
  // TASK-0211 (REQ-063-001). Same reasoning as `externalLeadId`/`landingPage` above: plain `string`
  // controlled-input state, '' -> omitted conversion happens once, in handleSave.
  clickId: string
  // TASK-0213 (REQ-063-001). Same reasoning as `externalLeadId`/`landingPage`/`clickId` above: plain
  // `string` controlled-input state, '' -> omitted conversion happens once, in handleSave. Bundled
  // as 5 fields at once (same bundling precedent as `fiscalCode`/`vatNumber`, TASK-0149) since all 5
  // standard UTM parameters share identical treatment.
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmContent: string
  utmTerm: string
  // TASK-0214 (REQ-063-001). Same reasoning as `utmSource`/etc. immediately above: plain `string`
  // controlled-input state, '' -> omitted conversion happens once, in handleSave. Bundled as 6
  // fields at once (same bundling precedent as the UTM bundle, TASK-0213) since all 6 are short
  // free-text labels with zero declared vocabulary.
  subCampaign: string
  channel: string
  partner: string
  module: string
  callCenterSource: string
  callCenterOperator: string
  leadType: string
  priority: string
  // TASK-0170 (REQ-088-001). Typed as plain `string` like every other member here (controlled
  // input), but unlike `priority` its EMPTY_FORM value is `''`, not a real classification — see
  // that constant below for why.
  temperature: string
  source: string
  // REQ-066-002. Same reasoning as `fiscalCode`/`vatNumber` above: plain `string` controlled-input
  // state, '' -> omitted conversion happens once, in handleSave.
  interestSector: string
  // F-110 (REQ-062-003): località facoltativa, stesso trattamento di `interestSector`.
  city: string
  province: string
  postalCode: string
  // F-116 (REQ-070-002): dipendenti (solo aziende) e campagna (solo in creazione); stringhe di input come `callAttempts`.
  employeesCount: string
  campaignId: string
  estimatedValue: string
  // TASK-0212 (REQ-063-001). Same reasoning as `estimatedValue` immediately above — NOT the plain
  // `string`/`if (truthy)` pattern used by `externalLeadId`/`landingPage`/`clickId` above, which is
  // wrong for a numeric field: this is controlled-input state parsed with `parseFloat` and guarded
  // with `!== ''` only at save time, exactly like `estimatedValue`, since `Lead.Cost` is a `decimal?`
  // monetary amount (see `Lead.Cost`'s remarks — its direct same-entity precedent is
  // `EstimatedValue`, not one of the free-text attribution fields).
  cost: string
  // TASK-0199 (REQ-116-001). Same reasoning as `estimatedValue` immediately above: plain `string`
  // controlled-input state, parsed with `parseInt` (not `parseFloat`) only at save time — this is a
  // whole-number count of contact attempts, not a monetary amount.
  callAttempts: string
  assignedAgentId: string
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  fiscalCode: '',
  vatNumber: '',
  externalLeadId: '',
  landingPage: '',
  clickId: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmContent: '',
  utmTerm: '',
  subCampaign: '',
  channel: '',
  partner: '',
  module: '',
  callCenterSource: '',
  callCenterOperator: '',
  leadType: 'person',
  priority: 'medium',
  // TASK-0170 (REQ-088-001). Deliberately `''` (the select's "—" / unclassified option), NOT a
  // default like `priority`'s `'medium'`: `Lead.Temperature` has no default (see `Lead.cs`) and a
  // new lead must be able to stay genuinely unclassified until someone picks a value.
  temperature: '',
  source: '',
  interestSector: '',
  city: '',
  province: '',
  postalCode: '',
  employeesCount: '',
  campaignId: '',
  estimatedValue: '',
  cost: '',
  callAttempts: '',
  assignedAgentId: '',
}

export function LeadFormPage({ mode }: Props) {
  const { id: idParam } = useParams<{ id: string }>()
  const id = parseInt(idParam ?? '0', 10)
  const navigate = useNavigate()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  // F-063: una chiave per tentativo di creazione; rigenerata dopo un successo.
  const idempotencyKey = useRef(newIdempotencyKey())
  const [error, setError] = useState<string | null>(null)
  const [duplicateConflict, setDuplicateConflict] = useState<
    { message: string; candidates: DuplicateCandidate[] } | null
  >(null)
  const [loadingData, setLoadingData] = useState(mode === 'edit')
  const [agents, setAgents] = useState<DirectoryUser[]>([])
  // Owner decision 2026-10-08: `source` is a closed, REQUIRED list served by `GET /api/leads/sources`
  // (no list hard-coded here). If the endpoint fails the field cannot be filled, so we surface the
  // error instead of pretending it is free text.
  const [sources, setSources] = useState<LeadSourceOption[]>([])
  const [sourcesError, setSourcesError] = useState<string | null>(null)
  // F-116: campagne attive per il selettore "Campagna" (solo in creazione).
  const [campaigns, setCampaigns] = useState<Campaign[]>([])

  useEffect(() => {
    if (mode !== 'create') return
    campaignsApi
      .list({ status: 'active', pageSize: 200 })
      .then(r => setCampaigns(r.items))
      .catch(() => setCampaigns([]))
  }, [mode])

  useEffect(() => {
    leadsApi
      .sources()
      .then(res => setSources(res))
      .catch(e => setSourcesError(e instanceof Error ? e.message : 'Errore nel caricamento delle fonti'))
  }, [])

  // TASK-0074: agent options for the "Agente assegnato" selector below. Same pattern as
  // CompanyFormPage.tsx/TASK-0059 — filtered server-side to role=EqualAgente, reused identically
  // rather than reinvented (see task card).
  useEffect(() => {
    userDirectoryApi.list({ roles: ['EqualAgente'], activeOnly: false, limit: 200 })
      .then(setAgents)
      .catch(() => setAgents([]))
  }, [])

  useEffect(() => {
    if (mode !== 'edit') return
    setLoadingData(true)
    leadsApi.get(id)
      .then(raw => {
        const lead = raw as unknown as LeadDetail
        setForm({
          firstName: lead.firstName ?? '',
          lastName: lead.lastName ?? '',
          companyName: lead.companyName ?? '',
          email: lead.email ?? '',
          phone: lead.phone ?? '',
          fiscalCode: lead.fiscalCode ?? '',
          vatNumber: lead.vatNumber ?? '',
          externalLeadId: lead.externalLeadId ?? '',
          landingPage: lead.landingPage ?? '',
          clickId: lead.clickId ?? '',
          utmSource: lead.utmSource ?? '',
          utmMedium: lead.utmMedium ?? '',
          utmCampaign: lead.utmCampaign ?? '',
          utmContent: lead.utmContent ?? '',
          utmTerm: lead.utmTerm ?? '',
          subCampaign: lead.subCampaign ?? '',
          channel: lead.channel ?? '',
          partner: lead.partner ?? '',
          module: lead.module ?? '',
          callCenterSource: lead.callCenterSource ?? '',
          callCenterOperator: lead.callCenterOperator ?? '',
          leadType: lead.leadType ?? 'person',
          priority: lead.priority ?? 'medium',
          // TASK-0170 (REQ-088-001). `?? ''`, not a default value — an existing lead with no
          // Temperature set must load back into the "—" / unclassified option, not silently pick up
          // a fabricated classification nobody chose.
          temperature: lead.temperature ?? '',
          source: lead.source ?? '',
          interestSector: lead.interestSector ?? '',
          city: lead.city ?? '',
          province: lead.province ?? '',
          postalCode: lead.postalCode ?? '',
          employeesCount: lead.employeesCount != null ? String(lead.employeesCount) : '',
          campaignId: '',
          estimatedValue: String(lead.estimatedValue ?? ''),
          cost: String(lead.cost ?? ''),
          callAttempts: String(lead.callAttempts ?? ''),
          assignedAgentId: lead.assignedAgentId != null ? String(lead.assignedAgentId) : '',
        })
      })
      .catch(e => {
        setError(e instanceof Error ? e.message : 'Errore nel caricamento')
      })
      .finally(() => setLoadingData(false))
  }, [mode, id])

  function field(key: keyof FormState) {
    return {
      value: form[key],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setForm(f => ({ ...f, [key]: e.target.value })),
    }
  }

  /**
   * TASK-0107: mirrors `CompanyFormPage.tsx`'s `handleSave(forceCreate)` — separated from the
   * `<form onSubmit>` handler below so the "Crea comunque" button (a plain button inside the
   * duplicate-conflict panel, not a form submit) can retry the same save with `forceCreate: true`.
   */
  async function handleSave(forceCreate = false): Promise<void> {
    setLoading(true)
    setError(null)
    if (!forceCreate) setDuplicateConflict(null)
    try {
      const data: LeadCreateRequest = { leadType: form.leadType }
      if (form.firstName) data.firstName = form.firstName
      if (form.lastName) data.lastName = form.lastName
      if (form.companyName) data.companyName = form.companyName
      if (form.email) data.email = form.email
      if (form.phone) data.phone = form.phone
      // TASK-0149 (REQ-066-001). Same `if (truthy)` guard as every field above, and it matters more
      // here than elsewhere: sending `fiscalCode: ''` on create would give the backend's duplicate
      // check an empty string rather than a missing field. (`NormalizeFiscalIdentifier` returns null
      // for blank input, so it would be ignored anyway — but on UPDATE, where `UpdateDetails`
      // assigns unconditionally, omitting is what makes "left the box empty" mean "cleared" instead
      // of writing an empty string into the column.)
      if (form.fiscalCode) data.fiscalCode = form.fiscalCode
      if (form.vatNumber) data.vatNumber = form.vatNumber
      // TASK-0198 (REQ-063-001). Same `if (truthy)` guard and same reasoning as `fiscalCode`/
      // `vatNumber` immediately above: `Lead.UpdateDetails` assigns `ExternalLeadId`
      // unconditionally, so omitting an empty box lets the backend store `NULL` instead of `''`.
      if (form.externalLeadId) data.externalLeadId = form.externalLeadId
      // TASK-0210 (REQ-063-001). Same `if (truthy)` guard and same reasoning as `externalLeadId`
      // immediately above: `Lead.UpdateDetails` assigns `LandingPage` unconditionally, so omitting an
      // empty box lets the backend store `NULL` instead of `''`.
      if (form.landingPage) data.landingPage = form.landingPage
      // TASK-0211 (REQ-063-001). Same `if (truthy)` guard and same reasoning as `externalLeadId`/
      // `landingPage` immediately above: `Lead.UpdateDetails` assigns `ClickId` unconditionally, so
      // omitting an empty box lets the backend store `NULL` instead of `''`.
      if (form.clickId) data.clickId = form.clickId
      // TASK-0213 (REQ-063-001). Same `if (truthy)` guard and same reasoning as `externalLeadId`/
      // `landingPage`/`clickId` immediately above: `Lead.UpdateDetails` assigns all 5 UTM fields
      // unconditionally, so omitting an empty box lets the backend store `NULL` instead of `''`.
      if (form.utmSource) data.utmSource = form.utmSource
      if (form.utmMedium) data.utmMedium = form.utmMedium
      if (form.utmCampaign) data.utmCampaign = form.utmCampaign
      if (form.utmContent) data.utmContent = form.utmContent
      if (form.utmTerm) data.utmTerm = form.utmTerm
      // TASK-0214 (REQ-063-001). Same `if (truthy)` guard and same reasoning as `externalLeadId`/
      // `landingPage`/`clickId`/the 5 UTM fields immediately above: `Lead.UpdateDetails` assigns all
      // 6 of these unconditionally, so omitting an empty box lets the backend store `NULL` instead
      // of `''`.
      if (form.subCampaign) data.subCampaign = form.subCampaign
      if (form.channel) data.channel = form.channel
      if (form.partner) data.partner = form.partner
      if (form.module) data.module = form.module
      if (form.callCenterSource) data.callCenterSource = form.callCenterSource
      if (form.callCenterOperator) data.callCenterOperator = form.callCenterOperator
      // `source` is required (closed list) on create AND update: always sent, never behind the
      // `if (truthy)` guard, so the backend can reject a missing choice instead of storing NULL.
      data.source = form.source
      // REQ-066-002. Same `if (truthy)` guard and same reasoning as `externalLeadId`/`landingPage`/
      // `clickId`/the UTM/attribution fields above: `Lead.UpdateDetails` assigns `InterestSector`
      // unconditionally, so omitting an empty box lets the backend store `NULL` instead of `''`.
      if (form.interestSector) data.interestSector = form.interestSector
      // F-110: località; il dominio rifiuta (400) una provincia non di 2 lettere o un CAP non di 5 cifre.
      if (form.city.trim()) data.city = form.city.trim()
      if (form.province.trim()) data.province = form.province.trim().toUpperCase()
      if (form.postalCode.trim()) data.postalCode = form.postalCode.trim()
      // F-116: dipendenti solo per le aziende (assegnati senza condizioni dal backend: vuoto = azzerato); campagna solo in creazione.
      if (form.leadType === 'company' && form.employeesCount !== '') data.employeesCount = parseInt(form.employeesCount, 10)
      if (mode === 'create' && form.campaignId !== '') data.campaignId = parseInt(form.campaignId, 10)
      if (form.estimatedValue !== '') data.estimatedValue = parseFloat(form.estimatedValue)
      // TASK-0212 (REQ-063-001). Same `!== ''` guard, `parseFloat` and unconditional-assignment
      // reasoning as `estimatedValue` immediately above — NOT the `if (truthy)` idiom
      // `externalLeadId`/`landingPage`/`clickId` use: `Lead.UpdateDetails` assigns `Cost`
      // unconditionally, and this is a `decimal?` monetary amount, not free text, so omitting an
      // empty box lets the backend store `NULL` instead of `0`.
      if (form.cost !== '') data.cost = parseFloat(form.cost)
      // TASK-0199 (REQ-116-001). Same `!== ''` guard and unconditional-assignment reasoning as
      // `estimatedValue` immediately above: `Lead.UpdateDetails` assigns `CallAttempts`
      // unconditionally, so omitting an empty box lets the backend store `NULL` instead of `0`.
      // `parseInt` (base 10), not `parseFloat`: this is a whole-number count.
      if (form.callAttempts !== '') data.callAttempts = parseInt(form.callAttempts, 10)
      // TASK-0054: the Priority <select> was already read into `form.priority` on load, but
      // handleSubmit never sent it back — so changing a lead's priority and saving silently had
      // no effect (UpdateDetails only assigns when the field arrives).
      data.priority = form.priority
      // TASK-0170 (REQ-088-001) / review fix: sent UNCONDITIONALLY, like `data.priority` immediately
      // above — NOT the `if (truthy)` idiom `fiscalCode`/`vatNumber` use. Those two guard the send so
      // "left the box empty" means "leave it as it was found" for a field this form doesn't always
      // fully own; Temperature must behave the other way: the "—" option is a real, selectable value
      // (unclassified), and a user who explicitly picks it is asking to CLEAR a prior classification,
      // not merely declining to change it. Since `Lead.UpdateDetails` GUARDS `Temperature`
      // (`if (temperature is not null) Temperature = temperature;`), an `if (form.temperature)` guard
      // here would make "—" a silent no-op with no way to ever deselect a lead's temperature once
      // set. Sending `''` unconditionally stores an empty string (not `NULL`) for "unclassified" —
      // both `LeadDetailPage.tsx`'s badge and this form's own `?? ''` load-mapping already treat an
      // empty/falsy value as "—", so the round trip is correct either way.
      data.temperature = form.temperature
      // TASK-0074: `Lead.UpdateDetails` assigns `AssignedAgentId` unconditionally (unlike
      // `Priority`, which is guarded) — any save that omits this field wipes the assigned agent,
      // even edits to unrelated fields. Always resend the current selector value (pre-filled on
      // load above) so an already-assigned lead round-trips its agent on every save.
      if (form.assignedAgentId) data.assignedAgentId = Number(form.assignedAgentId)
      if (mode === 'create' && forceCreate) data.forceCreate = true
      if (mode === 'create') {
        const res = await leadsApi.create(data, idempotencyKey.current)
        idempotencyKey.current = newIdempotencyKey()
        // F-080: l'esito delle regole di proprietà (se hanno deciso) viaggia verso la scheda del nuovo lead.
        navigate('/leads/' + res.id, { state: { ownershipDecision: res.ownershipDecision } })
      } else {
        await leadsApi.update(id, data)
        navigate('/leads/' + id)
      }
    } catch (err) {
      if (err instanceof DuplicateConflictError) {
        setDuplicateConflict({ message: err.message, candidates: err.candidates })
        setLoading(false)
        return
      }
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio')
      setLoading(false)
    }
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    await handleSave()
  }

  if (loadingData) {
    return <LoadingState />
  }

  return (
    <Page>
      <PageHeader
        title={mode === 'create' ? 'Nuovo Lead' : 'Modifica Lead'}
        icon="person_search"
        back={{ to: '/leads', label: 'Tutti i lead' }}
      />

      {duplicateConflict ? (
        <Callout tone="warning" icon="report">
          <div className="mb-2 md-typescale-emphasized-body-medium">{duplicateConflict.message}</div>
          <ul className="list-unstyled mb-3">
            {duplicateConflict.candidates.map((c, i) => (
              <li key={`${c.entityType}-${c.id}-${i}`} className="mb-1">
                <StatusPill tone="neutral" className="me-2">{c.entityType}</StatusPill>
                ID {c.id} — campo corrispondente: <strong>{c.matchedField}</strong>
                {' '}
                <a
                  href={candidateApiUrl(c)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-2"
                >
                  Vedi record esistente
                </a>
              </li>
            ))}
          </ul>
          <div className="d-flex gap-2">
            <Button
              variant="tonal"
              size="sm"
              disabled={loading}
              onClick={() => void handleSave(true)}
            >
              {loading ? 'Creazione...' : 'Crea comunque'}
            </Button>
            <Button
              variant="text"
              size="sm"
              disabled={loading}
              onClick={() => setDuplicateConflict(null)}
            >
              Annulla
            </Button>
          </div>
        </Callout>
      ) : (
        error && <Callout tone="error">{error}</Callout>
      )}

      <Card variant="outlined">
        <CardContent>
          <form onSubmit={e => void handleSubmit(e)}>
            {/* Row 1: name fields */}
            <FormGrid columns={3}>
              <div>
                <TextField id="lf-firstName" label="Nome *" type="text" required {...field('firstName')} />
              </div>
              <div>
                <TextField id="lf-lastName" label="Cognome" type="text" {...field('lastName')} />
              </div>
              <div>
                <TextField id="lf-companyName" label="Azienda" type="text" {...field('companyName')} />
              </div>
            </FormGrid>

            {/* Row 2: contact */}
            <FormGrid className="mt-3">
              <div>
                <TextField id="lf-email" label="Email" type="email" {...field('email')} />
              </div>
              <div>
                <TextField id="lf-phone" label="Telefono" type="text" {...field('phone')} />
              </div>
            </FormGrid>

            {/* Row 2b: fiscal identifiers — TASK-0149 (REQ-066-001). Placed immediately after
                Email/Telefono because these four fields are exactly the set the backend's duplicate
                check reads, so the form groups them the way the check treats them. Same
                riga a due colonne con TextField and same `field(...)` spread as the
                contact row above; `type="text"` for both, since neither is numeric (a Codice Fiscale
                is alphanumeric and a Partita IVA is a fixed-width digit STRING whose leading zeros
                `type="number"` would eat). Deliberately no maxLength/pattern validation: the backend
                column accepts 20 characters and does not validate the format either — see
                Lead.FiscalCode's remarks — and a lead is precisely the record where a
                half-transcribed identifier still needs to be storable. */}
            <FormGrid className="mt-3">
              <div>
                <TextField id="lf-fiscalCode" label="Codice Fiscale" type="text" {...field('fiscalCode')} />
              </div>
              <div>
                <TextField id="lf-vatNumber" label="Partita IVA" type="text" {...field('vatNumber')} />
              </div>
            </FormGrid>

            {/* Row 2c: external lead id (TASK-0198) + landing page (TASK-0210), REQ-063-001. Same
                input shape as the fiscal identifiers row above (`type="text"`, no maxLength/pattern
                — free text, no vocabulary), modeled exactly on "Codice Fiscale". A separate row
                rather than folded into Row 2b: these are provenance/attribution fields (the id an
                external lead-generation platform — e.g. Meta/Google Lead Ads — assigned to this
                lead, and the URL of the page on which the lead converted), not among the four
                fields Row 2b's own comment documents as exactly what the backend's duplicate check
                reads (Email/Phone/FiscalCode/VatNumber) — neither ExternalLeadId nor LandingPage
                participates in that check. */}
            <FormGrid className="mt-3">
              <div>
                <TextField id="lf-externalLeadId" label="ID Lead Esterno" type="text" {...field('externalLeadId')} />
              </div>
              {/* TASK-0210 (REQ-063-001). Same input shape as ID Lead Esterno immediately to the
                  left (`type="text"`, no maxLength/pattern — free text, no vocabulary), modeled
                  exactly on it: another provenance/attribution field, not one of Row 2b's
                  duplicate-check fields, so it lives in this same row rather than Row 2b.
                  `type="text"`, not `type="url"`, matching this form's existing convention of no
                  native HTML5 validation on text fields. */}
              <div>
                <TextField id="lf-landingPage" label="Landing Page" type="text" {...field('landingPage')} />
              </div>
            </FormGrid>

            {/* Row 2d: click id (TASK-0211), REQ-063-001. Same reasoning as Row 2c immediately
                above: a provenance/attribution field (the opaque click-tracking token a paid-ads
                platform — e.g. Google Ads' gclid, Meta's fbclid — attaches to a conversion), not one
                of Row 2b's duplicate-check fields, so it gets its own row rather than folding into
                Row 2b. Same input shape as ID Lead Esterno/Landing Page (`type="text"`, no
                maxLength/pattern — free text, no vocabulary). A separate row rather than a third
                column in Row 2c: this form's existing rows are all two-column (colonna da 6/12 x2). */}
            <FormGrid className="mt-3">
              <div>
                <TextField id="lf-clickId" label="Click ID" type="text" {...field('clickId')} />
              </div>
            </FormGrid>

            {/* Row 2e/2f: the 5 standard UTM tracking parameters — TASK-0213 (REQ-063-001),
                bundled as ONE task (same bundling precedent as Codice Fiscale/Partita IVA, Row 2b
                above, TASK-0149) rather than 5 separate increments: all 5 are the identical UTM
                tracking concept with identical shape/provenance. Same input shape as ID Lead
                Esterno/Landing Page/Click ID above (`type="text"`, no maxLength/pattern — free
                text, no vocabulary) and same reasoning for living in their own rows rather than
                Row 2b: provenance/attribution fields, not among the four fields the duplicate
                check reads. Split 3+2 across two rows (colonna da 4/12 x3, then x2) rather than one
                crammed 5-column row, matching the requirement's own order (Source, Medium,
                Campaign, Content, Term). */}
            <FormGrid columns={3} className="mt-3">
              <div>
                <TextField id="lf-utmSource" label="UTM Source" type="text" {...field('utmSource')} />
              </div>
              <div>
                <TextField id="lf-utmMedium" label="UTM Medium" type="text" {...field('utmMedium')} />
              </div>
              <div>
                <TextField id="lf-utmCampaign" label="UTM Campaign" type="text" {...field('utmCampaign')} />
              </div>
            </FormGrid>
            <FormGrid columns={3} className="mt-3">
              <div>
                <TextField id="lf-utmContent" label="UTM Content" type="text" {...field('utmContent')} />
              </div>
              <div>
                <TextField id="lf-utmTerm" label="UTM Term" type="text" {...field('utmTerm')} />
              </div>
            </FormGrid>

            {/* Row 2g/2h: the LAST 6 attribution residuals — TASK-0214 (REQ-063-001), bundled as
                ONE task (same bundling precedent as the UTM rows immediately above, TASK-0213)
                rather than 6 separate increments: all 6 are short free-text labels (channel/
                partner/operator names) with zero declared vocabulary in the requirement's text.
                Same input shape as the UTM/Click ID/Landing Page fields above (`type="text"`, no
                maxLength/pattern — free text, no vocabulary) and same reasoning for living in
                their own rows rather than Row 2b: provenance/attribution fields, not among the
                four fields the duplicate check reads. Split 3+3 across two rows (colonna da 4/12 x3
                each), same requirement-text order (Sub-campaign, Channel, Partner, Module, Call
                Center Source, Call Center Operator). This closes REQ-063-001 COMPLETELY. */}
            <FormGrid columns={3} className="mt-3">
              <div>
                <TextField id="lf-subCampaign" label="Sottocampagna" type="text" {...field('subCampaign')} />
              </div>
              <div>
                <TextField id="lf-channel" label="Canale" type="text" {...field('channel')} />
              </div>
              <div>
                <TextField id="lf-partner" label="Partner" type="text" {...field('partner')} />
              </div>
            </FormGrid>
            <FormGrid columns={3} className="mt-3">
              <div>
                <TextField id="lf-module" label="Modulo" type="text" {...field('module')} />
              </div>
              <div>
                <TextField id="lf-callCenterSource" label="Call Center di Provenienza" type="text" {...field('callCenterSource')} />
              </div>
              <div>
                <TextField id="lf-callCenterOperator" label="Operatore Call Center" type="text" {...field('callCenterOperator')} />
              </div>
            </FormGrid>

            {/* Row 3: type/priority/source/value */}
            <FormGrid columns={4} className="mt-3">
              <div>
                <Select id="lf-leadType" label="Tipo Lead" {...field('leadType')}>
                  <option value="person">Persona</option>
                  <option value="company">Azienda</option>
                </Select>
              </div>
              <div>
                <Select id="lf-priority" label="Priorità" {...field('priority')}>
                  <option value="low">Bassa</option>
                  <option value="medium">Normale</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </Select>
              </div>
              <div>
                <Select id="lf-source" label="Fonte *"
                  required
                  aria-required="true"
                  aria-invalid={sourcesError ? true : undefined}
                  aria-describedby={sourcesError ? 'lf-source-error' : undefined}
                  {...field('source')}
                >
                  <option value="">Seleziona…</option>
                  {/* A saved value outside the current list (legacy data) stays visible and selected;
                      saving it is rejected by the backend until the user picks a valid source. */}
                  {form.source && !sources.some(o => o.code === form.source) && (
                    <option value={form.source}>{form.source} (non più valida)</option>
                  )}
                  {sources.map(o => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </Select>
                {sourcesError && (
                  <div id="lf-source-error" role="alert" className="text-danger md-typescale-body-small mt-1">
                    Impossibile caricare l'elenco delle fonti: {sourcesError}
                  </div>
                )}
              </div>
              <div>
                <TextField id="lf-estimatedValue" label="Valore Stimato €" type="number" min="0" step="0.01" {...field('estimatedValue')} />
              </div>
            </FormGrid>

            {/* Row 3b: temperature/call attempts/cost/interest sector — TASK-0170 (REQ-088-001) /
                TASK-0199 (REQ-116-001) / TASK-0212 (REQ-063-001) / REQ-066-002. A new row rather than
                a 5th colonna da 3/12 folded into Row 3 above (that row is already a full 4-column 12/12
                grid). Unlike `lf-priority`, `lf-temperature`'s `<select>` has an empty/"non
                classificato" first option and no `required`: `Lead.Temperature` has no default (see
                `form.priority`'s `'medium'` default above vs. `EMPTY_FORM.temperature`'s `''`), so
                staying unclassified must be a real, selectable state, not just the absence of a
                choice. `lf-callAttempts` is placed alongside it as the row's other classification/
                count field — same `type="number" min="0"` shape as `lf-estimatedValue` above, but no
                `step` (a whole-number count, not a currency amount). `lf-cost` follows: same
                `type="number" min="0" step="0.01"` shape as `lf-estimatedValue` (a currency amount,
                not a count) — `Lead.Cost` is `decimal?` like `EstimatedValue`, its direct same-entity
                precedent, not one of the free-text attribution fields in Row 2c/2d. `lf-interestSector`
                (REQ-066-002) fills this row's 4th slot — free text, no vocabulary, short label like
                `lf-source` (free text, unlike `lf-source` which is a closed-list `<select>`) — placed here, not in Row 3 alongside `lf-source`, because this row still had
                one open colonna da 3/12 and Row 3 did not. */}
            <FormGrid columns={4} className="mt-3">
              <div>
                <Select id="lf-temperature" label="Temperatura" {...field('temperature')}>
                  <option value="">—</option>
                  <option value="cold">Freddo</option>
                  <option value="warm">Tiepido</option>
                  <option value="hot">Caldo</option>
                  <option value="very_hot">Molto caldo</option>
                </Select>
              </div>
              <div>
                <TextField id="lf-callAttempts" label="Tentativi di Chiamata" type="number" min="0" {...field('callAttempts')} />
              </div>
              <div>
                <TextField id="lf-cost" label="Costo Lead €" type="number" min="0" step="0.01" {...field('cost')} />
              </div>
              <div>
                <TextField id="lf-interestSector" label="Settore di Interesse" type="text" {...field('interestSector')} />
              </div>
            </FormGrid>

            {/* F-110 (REQ-062-003): località del lead */}
            <FormGrid columns={4} className="mt-3">
              <div className="md-span-2">
                <TextField id="lf-city" label="Città" type="text" maxLength={100} {...field('city')} />
              </div>
              <div>
                <TextField id="lf-province" label="Provincia (sigla)" type="text" maxLength={2} uppercase placeholder="MI" {...field('province')} />
              </div>
              <div>
                <TextField id="lf-postalCode" label="CAP" type="text" inputMode="numeric" maxLength={5} pattern="[0-9]{5}" placeholder="20100" {...field('postalCode')} />
              </div>
            </FormGrid>

            {/* F-116 (REQ-070-002): dimensione aziendale e campagna, usate dall'instradamento */}
            <FormGrid columns={3} className="mt-3">
              {form.leadType === 'company' && (
                <div>
                  <TextField id="lf-employeesCount" label="Dipendenti" type="number" min={0} step={1} placeholder="Es. 25" {...field('employeesCount')} />
                </div>
              )}
              {mode === 'create' && (
                <div>
                  <Select id="lf-campaignId" label="Campagna" {...field('campaignId')}>
                    <option value="">Nessuna campagna</option>
                    {campaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </div>
              )}
            </FormGrid>

            {/* Row 4: assigned agent */}
            <FormGrid columns={3} className="mt-3">
              <div>
                <Select id="lf-assignedAgentId" label="Agente assegnato" {...field('assignedAgentId')}>
                  <option value="">Nessun agente</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Select>
              </div>
            </FormGrid>

            {/* Actions */}
            <div className="mt-4 d-flex gap-2">
              <Button type="submit" variant="filled" icon="save" disabled={loading}>
                {loading ? 'Salvataggio...' : 'Salva'}
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/leads')}
                disabled={loading}
              >
                Annulla
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Page>
  )
}
